// import Cookies from "js-cookie";
import { csrfFetch } from '../utils/csrfFetch';
// Action Types
const LOAD_COMPANIES = "companies/LOAD_COMPANIES";
const CREATE_COMPANY = "companies/CREATE_COMPANY";
const UPDATE_COMPANY = "companies/UPDATE_COMPANY";
const SET_SINGLE_COMPANY = "companies/SET_SINGLE_COMPANY";
const CREATE_ACCOUNT = "companies/CREATE_ACCOUNT";
const UPDATE_ACCOUNT = "companies/UPDATE_ACCOUNT";
const DELETE_ACCOUNT = "companies/DELETE_ACCOUNT";

// Action Creators
export const loadCompanies = (companies) => {
  console.log('LOAD_COMPANIES action with payload:', companies);
  return {
    type: LOAD_COMPANIES,
    payload: companies,
  };
};

export const createCompanyAction = (company) => ({
  type: CREATE_COMPANY,
  payload: company,
});

export const updateCompanyAction = (company) => ({
  type: UPDATE_COMPANY,
  payload: company,
});

export const setSingleCompanyAction = (company) => ({
  type: SET_SINGLE_COMPANY,
  payload: company,
});

export const createAccountAction = (account) => ({
  type: CREATE_ACCOUNT,
  payload: account,
});

export const updateAccountAction = (account) => ({
  type: UPDATE_ACCOUNT,
  payload: account,
});

export const deleteAccountAction = (accountId, companyId) => ({
  type: DELETE_ACCOUNT,
  payload: { accountId, companyId },
});

// Thunks
export const fetchCompaniesAdmin = () => async (dispatch) => {
  try {
    console.log('fetchCompaniesAdmin: Starting API call');

    // Explicitly use csrfFetch with proper credentials and headers
    const response = await csrfFetch('/api/companies', {
      method: 'GET'
    });

    console.log('fetchCompaniesAdmin: Response status:', response.status);

    if (response.ok) {
      const companies = await response.json();
      console.log('fetchCompaniesAdmin: Received companies:', companies);

      if (!companies || !Array.isArray(companies)) {
        console.error('fetchCompaniesAdmin: Response is not an array:', companies);
        // If we get a non-array, create an empty array to avoid errors
        dispatch(loadCompanies([]));
        return [];
      }

      dispatch(loadCompanies(companies));
      return companies;
    } else {
      const error = await response.json().catch(e => ({ error: `Failed to parse error response: ${e}` }));
      console.error('fetchCompaniesAdmin: Error response:', error);
      throw new Error(error.error || 'Failed to fetch companies');
    }
  } catch (error) {
    console.error('Error fetching companies:', error);
    // Dispatch empty array to avoid undefined errors in components
    dispatch(loadCompanies([]));
    throw error;
  }
};

// New thunk for fetching companies directly as a fallback
export const fetchCompaniesFallback = () => async (dispatch) => {
  try {
    console.log('Attempting fallback fetch for companies...');

    // Try direct fetch as a fallback for CSRF issues
    const fullDataResp = await fetch('/api/companies', {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (fullDataResp.ok) {
      const fullData = await fullDataResp.json();
      console.log('Fallback fetch successful:', fullData);

      if (Array.isArray(fullData) && fullData.length > 0) {
        dispatch(loadCompanies(fullData));
        return fullData;
      } else {
        console.error('Fallback fetch returned invalid data:', fullData);
        return [];
      }
    } else {
      console.error('Fallback fetch failed with status:', fullDataResp.status);
      return [];
    }
  } catch (error) {
    console.error('Error in fallback fetch:', error);
    return [];
  }
};

// Test endpoint thunk to check API status
export const testCompaniesApi = () => async () => {
  try {
    console.log('Testing company API endpoint...');
    const testResp = await csrfFetch('/api/companies/test');

    if (testResp.ok) {
      const testData = await testResp.json();
      console.log('API Test Response:', testData);
      return testData;
    } else {
      console.error('Test API call failed with status:', testResp.status);
      return null;
    }
  } catch (error) {
    console.error('Error testing API:', error);
    return null;
  }
};

// Seed test company thunk
export const seedTestCompany = () => async (dispatch) => {
  try {
    console.log('Seeding test company...');
    const response = await csrfFetch('/api/companies/seed-test', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (response.ok) {
      const data = await response.json();
      console.log('Seed test company response:', data);

      // If the seeding returned a company, add it to the store
      if (data.company) {
        dispatch(createCompanyAction(data.company));
      }

      return data;
    } else {
      console.error('Seed test company failed with status:', response.status);
      return null;
    }
  } catch (error) {
    console.error('Error seeding test company:', error);
    return null;
  }
};

export const fetchCompanyById = (id) => async (dispatch, getState) => {
  try {
    console.log(`fetchCompanyById: Fetching company ${id}`);

    // First check if we already have the company in the Redux store
    const companies = getState().companies;
    const companyInStore = Array.isArray(companies) ?
      companies.find(c => c.id === parseInt(id)) : null;

    if (companyInStore) {
      console.log(`Company ${id} found in Redux store`);
      dispatch(setSingleCompanyAction(companyInStore));
      return companyInStore;
    }

    // Try getting complete data from test endpoint first
    try {
      console.log("Trying test endpoint for complete data");
      const testResp = await csrfFetch('/api/companies/test');
      if (testResp.ok) {
        const testData = await testResp.json();

        if (testData.complete_data && Array.isArray(testData.complete_data)) {
          const companyFromTest = testData.complete_data.find(c => c.id === parseInt(id));
          if (companyFromTest) {
            console.log("Found company in test endpoint data");
            dispatch(setSingleCompanyAction(companyFromTest));
            return companyFromTest;
          }
        }
      }
    } catch (testError) {
      console.error("Error fetching from test endpoint:", testError);
    }

    // Try direct API call as last resort
    console.log("Trying direct API call to get company");
    const directResp = await csrfFetch(`/api/companies/${id}`);
    if (directResp.ok) {
      const directData = await directResp.json();
      console.log("Found company from direct API call:", directData);
      dispatch(setSingleCompanyAction(directData));
      return directData;
    }

    throw new Error(`Company with ID ${id} not found`);
  } catch (error) {
    console.error(`Error fetching company ${id}:`, error);
    throw error;
  }
};

export const fetchCompaniesUser = (accountId) => async (dispatch) => {
  try {
    const response = await csrfFetch('/api/companies/');

    if (response.ok) {
      const companies = await response.json();
      dispatch(loadCompanies(companies));
      return companies;
    } else {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch companies');
    }
  } catch (error) {
    console.error('Error fetching companies:', error);
    throw error;
  }
};

export const createCompany = (companyData) => async (dispatch) => {
  try {
    const response = await csrfFetch('/api/companies/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(companyData)
    });

    if (response.ok) {
      const newCompany = await response.json();
      dispatch(createCompanyAction(newCompany));
      return newCompany;
    } else {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create company');
    }
  } catch (error) {
    console.error('Error creating company:', error);
    throw error;
  }
};

export const updateCompany = (id, companyData) => async (dispatch) => {
  try {
    const response = await csrfFetch(`/api/companies/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(companyData)
    });

    if (response.ok) {
      const updatedCompany = await response.json();
      dispatch(updateCompanyAction(updatedCompany));
      return updatedCompany;
    } else {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update company');
    }
  } catch (error) {
    console.error('Error updating company:', error);
    throw error;
  }
};

export const createAccount = (accountData) => async (dispatch) => {
  try {
    const response = await csrfFetch('/api/accounts/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(accountData)
    });

    if (response.ok) {
      const newAccount = await response.json();
      dispatch(createAccountAction(newAccount));
      return newAccount;
    } else {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create account');
    }
  } catch (error) {
    console.error('Error creating account:', error);
    throw error;
  }
};

export const updateAccount = (accountId, accountData) => async (dispatch) => {
  try {
    const response = await csrfFetch(`/api/accounts/${accountId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(accountData)
    });

    if (response.ok) {
      const updatedAccount = await response.json();
      dispatch(updateAccountAction(updatedAccount));
      return updatedAccount;
    } else {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update account');
    }
  } catch (error) {
    console.error('Error updating account:', error);
    throw error;
  }
};

export const deleteAccount = (accountId, companyId) => async (dispatch) => {
  try {
    const response = await csrfFetch(`/api/accounts/${accountId}`, {
      method: 'DELETE'
    });

    if (response.ok) {
      dispatch(deleteAccountAction(accountId, companyId));
      return { success: true };
    } else {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete account');
    }
  } catch (error) {
    console.error('Error deleting account:', error);
    throw error;
  }
};

// Reducer
const initialState = [];

export default function companiesReducer(state = initialState, action) {
  // console.log('companiesReducer called with action:', action.type, 'and payload:', action.payload);

  switch (action.type) {
    case LOAD_COMPANIES:
      console.log('LOAD_COMPANIES reducer with payload:', action.payload);
      return action.payload || [];

    case SET_SINGLE_COMPANY: {
      // First check if the company already exists in the state
      const existingIndex = state.findIndex(company => company.id === action.payload.id);

      // If it exists, replace it; otherwise, add it
      if (existingIndex >= 0) {
        return state.map(company =>
          company.id === action.payload.id ? action.payload : company
        );
      } else {
        return [...state, action.payload];
      }
    }

    case CREATE_COMPANY:
      return [...state, action.payload];

    case UPDATE_COMPANY:
      return state.map(company =>
        company.id === action.payload.id ? action.payload : company
      );

    case CREATE_ACCOUNT: {
      return state.map(company => {
        if (company.id === action.payload.company_id) {
          // Create a new accounts array if it doesn't exist
          const updatedAccounts = company.accounts ? [...company.accounts, action.payload] : [action.payload];
          return {
            ...company,
            accounts: updatedAccounts
          };
        }
        return company;
      });
    }

    case UPDATE_ACCOUNT: {
      return state.map(company => {
        if (company.id === action.payload.company_id && company.accounts) {
          return {
            ...company,
            accounts: company.accounts.map(account =>
              account.id === action.payload.id ? action.payload : account
            )
          };
        }
        return company;
      });
    }

    case DELETE_ACCOUNT: {
      const { accountId, companyId } = action.payload;
      return state.map(company => {
        if (company.id === companyId && company.accounts) {
          return {
            ...company,
            accounts: company.accounts.filter(account => account.id !== accountId)
          };
        }
        return company;
      });
    }

    default:
      return state;
  }
}
